import { ReactNode } from 'react';

import { BoardThemes } from '@exports/ChessBoard';
import { cx } from '@libs/classnames';
import { pluralize } from '@libs/i18n';
import { Button } from '@ui/components/Button';
import { Card } from '@ui/components/Card';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { PieChartIcon } from '@ui/icons/PieChartIcon/PieChartIcon';

import { GameView } from '@features/game/ui/GameView/GameView';
import { ClockControlType, MyClubStatsType } from '@features/shared/api/typings';
import { TIME_CONTROL } from '@features/shared/constants';

import styles from './ClubMyStats.module.css';

const gamesPlural = (gamesCount: number) =>
  pluralize(
    {
      many: '{number}\u00A0партий',
      few: '{number}\u00A0партии',
      one: '{number}\u00A0партия',
      zero: '{number}\u00A0партий',
    },
    gamesCount,
  );

const tournamentsPlural = (gamesCount: number) =>
  pluralize(
    {
      many: '{number}\u00A0турниров',
      few: '{number}\u00A0турнира',
      one: '{number}\u00A0турнир',
      zero: '{number}\u00A0турниров',
    },
    gamesCount,
  );

const winsPlural = (gamesCount: number) =>
  pluralize(
    {
      many: '{number}\u00A0побед',
      few: '{number}\u00A0победы',
      one: '{number}\u00A0победа',
      zero: '{number}\u00A0побед',
    },
    gamesCount,
  );

export type Props = {
  className?: string;
  stats: MyClubStatsType;
  boardTheme?: BoardThemes;
  onGameClick: (gameId: string, tournamentId: string) => void;
  enhanceHref?: string;
  renderQuickPeekWidget?: (userId: string) => ReactNode;
};

export function ClubMyStats({
  className,
  stats,
  boardTheme,
  onGameClick,
  enhanceHref,
  renderQuickPeekWidget,
}: Props) {
  const isMobile = useMediaQuery() === 'mobile';

  const whiteWinsPercent =
    stats.winsAsWhiteCount > 0
      ? Math.floor((stats.winsAsWhiteCount / stats.totalWinCount) * 100)
      : 0;
  const blackWinsPercent =
    stats.winsAsBlackCount > 0
      ? Math.floor((stats.winsAsBlackCount / stats.totalWinCount) * 100)
      : 0;

  return (
    <Card className={cx(styles.stats, className)} flat>
      <div className={styles.header}>
        <div className={styles.title}>Моя статистика в клубе</div>
        {enhanceHref && (
          <a href={enhanceHref} target="_blank" rel="noreferrer">
            <Button variant="slim">Улучшить{isMobile ? '' : ' статистику'} ›</Button>
          </a>
        )}
      </div>

      <div className={styles.statsContainer}>
        <div className={styles.clockControlStats}>
          <ClockControlStats {...stats.blitz} />
          <ClockControlStats {...stats.rapid} />
        </div>

        <div className={styles.separator} />

        <div className={styles.totalStats}>
          <div className={styles.eventsCount}>
            <div className={styles.gamesCount}>{gamesPlural(stats.totalGamesCount)}</div>
            <div className={styles.tournamentsCount}>
              {tournamentsPlural(stats.totalTournamentsCount)}
            </div>
          </div>

          <div className={styles.winCount}>
            <div className={styles.totalWins}>{winsPlural(stats.totalWinCount)}</div>
            <div className={styles.winsBySide}>
              <div className={styles.wins}>
                <div className={styles.side}>Белыми</div>
                <div className={styles.percentage}>
                  <PieChartIcon className={styles.pieChart} fillPercent={whiteWinsPercent} />
                  {whiteWinsPercent}% · {stats.winsAsWhiteCount}
                </div>
              </div>
              <div className={styles.wins}>
                <div className={styles.side}>Черными</div>
                <div className={styles.percentage}>
                  <PieChartIcon className={styles.pieChart} fillPercent={blackWinsPercent} />
                  {blackWinsPercent}% · {stats.winsAsBlackCount}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {stats.recentGames && stats.recentGames.length > 0 && (
        <>
          <div className={styles.separator} />
          <div className={styles.recentGames}>
            <div className={styles.title}>Недавние игры</div>

            <HorizontalScroll enableForDesktop className={styles.games}>
              {stats.recentGames.map((gameData) => (
                <GameView
                  key={gameData.gameId}
                  className={styles.game}
                  theme={boardTheme}
                  tournamentId={gameData.tournamentId}
                  gameId={gameData.gameId}
                  whitePlayer={gameData.whitePlayer}
                  blackPlayer={gameData.blackPlayer}
                  orientation={gameData.mySide}
                  onClick={onGameClick}
                  result={gameData.result}
                  fen={gameData.fen}
                  renderQuickPeekWidget={renderQuickPeekWidget}
                />
              ))}
            </HorizontalScroll>
          </div>
        </>
      )}
    </Card>
  );
}

type ClockControlStatsProps = {
  clockControlType: ClockControlType;
  clubRank: number | null;
  gamesCount: number;
  winCount: number;
  drawCount: number;
};

function ClockControlStats({
  clockControlType,
  clubRank,
  gamesCount,
  winCount,
  drawCount,
}: ClockControlStatsProps) {
  const winPercent = winCount > 0 ? Math.floor((winCount / gamesCount) * 100) : 0;
  const drawPercent = drawCount > 0 ? Math.floor((drawCount / gamesCount) * 100) : 0;

  return (
    <div className={styles.clockControlStat}>
      <div className={styles.title}>{TIME_CONTROL[clockControlType]}</div>
      <div className={styles.clubRank}>{clubRank === null ? '?' : clubRank} место</div>

      <div className={styles.results}>
        {gamesCount === 0 ? (
          <span className={styles.placeholder}>Нет игр</span>
        ) : (
          <>
            <span className={styles.win}>Победы: {winPercent}%</span>
            <span className={styles.draw}>Ничьи: {drawPercent}%</span>
          </>
        )}
      </div>
    </div>
  );
}
